import React from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import { connect } from 'react-redux';
import { useOnlineStatus, useWindowSize } from '../../lib/hooks';

import { showModal, hideReport, showReport } from '../../actions/modal';
import styled from 'styled-components';
import { Routes, Route } from 'react-router-dom';

import { getExercisePrompt, getExerciseTypeFromName } from '../../lib/exercise';
import { HOMEWORK_EXECUTION, RESULTS, PAUSE, START_PAGE } from '../../constants/routes';
import { RATING_MODAL, REPORT_MODAL } from '../modal/modal-types';

import Difficulty from '../../components/difficulty';
import Skeleton from '../../components/skeleton';

import Section, { BreakText, Text, Link, TextBold, Row, SectionList } from './section';
import UserAvatar from './assets/userAvatar.svg?react';
import SupportIcon from './assets/support.svg?react';
import ExerciseIcon from './assets/informationsource.svg?react';
import EventIcon from './assets/event.svg?react';
import EventCancelledIcon from './assets/eventCancelled.svg?react';
import Stack from '../../components/stack';
import Report from '../../components/report';
import { parseDate } from '../../lib/utils';
import { parseHomeworkId } from '../../lib/homework';

import { BUILD_YEAR } from '../../config';

const Footer = styled.div`
    flex-shrink: 0;
`;

const Copyright = styled.div`
    display: flex;
    align-items: center;
    height: 56px;
    margin: 0 32px 0 24px;
    border-top: ${(props) => props.supportOpened && '1px solid var(--gray200)'};
    font-size: 12px;
    letter-spacing: 0.32px;
    line-height: 16px;
    color: var(--gray700);
    box-sizing: border-box;
`;

const Content = styled.div`
    padding: 0 ${(props) => props.isLoading && '32px'};
    overflow-y: auto;

    &::-webkit-scrollbar {
        /* Chrome */
        display: none;
    }

    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
`;

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: calc(100vh - 80px);
    box-sizing: border-box;
    padding: 84px 0 0 0;
    background-color: var(--gray50);
    box-shadow: inset 1px 1px 2px 0 rgba(66, 69, 72, 0.35);
`;

const TitleContainer = styled.div`
    padding: 0 24px 16px 24px;
`;

const DescriptionContainer = styled.div`
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.32px;
`;

const ExerciseInfo = styled.div`
    display: flex;
    flex-direction: column;
    background-color: var(--gray200);
    border-radius: 4px;
    margin: 0 -8px 0 -8px;
    padding: 8px;
`;

export const Title = styled.div`
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 0.3px;
    line-height: 24px;
`;

const SubTitle = styled.p`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: 0.16px;
`;

export const ExerciseType = styled.div`
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 14px;
    margin-bottom: 4px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.16px;
    line-height: 20px;
`;

const skeletonItems = [{ width: 60 }, {}, {}, { width: 0, height: 'none' }];

export const ExerciseSection = ({ currentExercise, singleAnswer }) => {
    return (
        <Section title="Esercizio" Icon={ExerciseIcon} opened={true}>
            <Stack gap="small">
                <ExerciseInfo>
                    <ExerciseType>{getExerciseTypeFromName(currentExercise.typeLabel)}</ExerciseType>
                    <Text>
                        <TextBold style={{ marginRight: '5px' }}>{'Cosa devi fare:'}</TextBold>
                        {getExercisePrompt(currentExercise, singleAnswer)}
                    </Text>
                </ExerciseInfo>
                <Stack gap="xs">
                    <Row>
                        <TextBold>{'Difficoltà:'}</TextBold>
                        <Difficulty level={currentExercise.difficulty} />
                    </Row>
                    <Row>
                        <TextBold>{'Esercizio (ID):'}</TextBold>
                        <Text>{`#${currentExercise.originalId || currentExercise.id}`}</Text>
                    </Row>
                </Stack>
            </Stack>
        </Section>
    );
};

ExerciseSection.propTypes = {
    currentExercise: PropTypes.object,
    singleAnswer: PropTypes.bool,
};

export const SidebarHomeworkSection = ({ title, homework, assignment, source, isPreview }) => {
    const homeworkId = parseHomeworkId(homework.id, homework.idCat, source);

    const isAssignment = !!assignment?.title;
    let executionType;
    if (isPreview) {
        executionType = 'Anteprima';
    } else {
        executionType = isAssignment ? 'Prova assegnata' : 'Prova in autonomia';
    }

    const creator = homework.ugc ? `${homework.creator?.firstName} ${homework.creator?.lastName}` : 'Zanichelli';

    return (
        <Section title={title} Icon={isAssignment ? EventIcon : EventCancelledIcon}>
            <Stack gap="small">
                <SubTitle>{executionType}</SubTitle>
                {isAssignment && <Text dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(homework.publicTitle) }} />}
                <Stack gap="xs">
                    <Row>
                        <TextBold>{'Creata da: '}</TextBold>
                        <Text>{creator}</Text>
                    </Row>
                    {homework.description && (
                        <Row>
                            <DescriptionContainer>
                                <TextBold>{'Descrizione: '}</TextBold>
                                <BreakText>{homework.description}</BreakText>
                            </DescriptionContainer>
                        </Row>
                    )}
                    {!!assignment?.teacherName && (
                        <Row>
                            <TextBold>{'Assegnata da:'}</TextBold>
                            <Text>{assignment.teacherName}</Text>
                        </Row>
                    )}
                    {!!assignment?.endDate && (
                        <Row>
                            <TextBold>{'Scadenza:'}</TextBold>
                            <Text>{parseDate(assignment.endDate, 'DD/MM/YYYY - HH:mm')}</Text>
                        </Row>
                    )}
                    <Row>
                        <TextBold>{'Prova (ID):'}</TextBold>
                        <Text id="homeworkId">{`#${homeworkId}`}</Text>
                    </Row>
                </Stack>
            </Stack>
        </Section>
    );
};

SidebarHomeworkSection.propTypes = {
    title: PropTypes.string,
    homework: PropTypes.object,
    assignment: PropTypes.object,
    source: PropTypes.string,
    isPreview: PropTypes.bool,
};

const SidebarContainer = ({
    applicationStatus,
    assignment,
    currentIndex,
    exercises,
    publicTitle,
    showModal,
    status,
    homeworkStatus,
    statusHttpStatus,
    authStatus,
    displayReport,
    hideReport,
    showReport,
    homework,
    source,
    singleAnswer,
    user,
    isPreview,
}) => {
    const currentExercise = exercises[currentIndex] || {};

    const isLoading = homeworkStatus.fetching || statusHttpStatus.fetching || authStatus.fetching;

    const { innerWidth } = useWindowSize();
    const isMobile = innerWidth < 1152;
    const onlineStatus = useOnlineStatus();

    const handleClickReport = () => {
        if (isMobile) {
            showModal(REPORT_MODAL);
            showReport();
            return;
        }
        if (displayReport) {
            hideReport();
            return;
        }
        showReport();
    };

    return (
        <Container>
            <Content isLoading={isLoading}>
                <Skeleton isLoading={isLoading} items={skeletonItems}>
                    {applicationStatus.completed && !homeworkStatus.error && (
                        <>
                            <Routes>
                                <Route
                                    path={RESULTS}
                                    element={
                                        <TitleContainer>
                                            <Title
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(assignment?.title || publicTitle),
                                                }}
                                            />
                                        </TitleContainer>
                                    }
                                />
                                <Route
                                    path={PAUSE}
                                    element={
                                        <TitleContainer>
                                            <Title
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(assignment?.title || publicTitle),
                                                }}
                                            />
                                        </TitleContainer>
                                    }
                                />
                                <Route
                                    path={START_PAGE}
                                    element={
                                        <TitleContainer>
                                            <Title
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(assignment?.title || publicTitle),
                                                }}
                                            />
                                        </TitleContainer>
                                    }
                                />
                                <Route
                                    path={HOMEWORK_EXECUTION}
                                    element={
                                        <TitleContainer>
                                            <Title
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(assignment?.title || publicTitle),
                                                }}
                                            />
                                        </TitleContainer>
                                    }
                                />
                            </Routes>
                            <Routes>
                                <Route
                                    path={RESULTS}
                                    element={
                                        <SidebarHomeworkSection
                                            title="Prova terminata"
                                            homework={homework}
                                            assignment={assignment}
                                            source={source}
                                            isPreview={isPreview}
                                        />
                                    }
                                />
                                <Route
                                    path={PAUSE}
                                    element={
                                        <SidebarHomeworkSection
                                            title={!status.resumed ? 'Prova in pausa' : 'Prova in corso'}
                                            homework={homework}
                                            assignment={assignment}
                                            source={source}
                                            isPreview={isPreview}
                                        />
                                    }
                                />
                                <Route
                                    path={START_PAGE}
                                    element={
                                        <SidebarHomeworkSection
                                            title="Prova selezionata"
                                            homework={homework}
                                            assignment={assignment}
                                            source={source}
                                            isPreview={isPreview}
                                        />
                                    }
                                />
                                <Route
                                    path={HOMEWORK_EXECUTION}
                                    element={
                                        <SectionList>
                                            {!assignment?.status?.error && (
                                                <>
                                                    <ExerciseSection currentExercise={currentExercise} singleAnswer={singleAnswer} />
                                                    <SidebarHomeworkSection
                                                        title={status.finished ? 'Prova terminata' : 'Prova in corso'}
                                                        homework={homework}
                                                        assignment={assignment}
                                                        source={source}
                                                        isPreview={isPreview}
                                                    />{' '}
                                                </>
                                            )}
                                        </SectionList>
                                    }
                                />
                            </Routes>
                        </>
                    )}
                </Skeleton>
                <Skeleton isLoading={isLoading} items={[{ width: 30 }, { width: 40 }]} direction="horizontal" />
            </Content>
            <Footer>
                {user.isLoggedIn && (
                    <Section title={`${user.firstName} ${user.lastName}`} Icon={UserAvatar}>
                        <Stack gap="small">
                            <Row>
                                <Text
                                    style={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {user.email}
                                </Text>
                            </Row>

                            {!!assignment?.className && (
                                <Row>
                                    <TextBold>{'Classe:'}</TextBold>
                                    <Text>{assignment.className}</Text>
                                </Row>
                            )}
                        </Stack>
                    </Section>
                )}
                <Report showReport={displayReport} />
                <Section title="Supporto" Icon={SupportIcon}>
                    <Stack gap="small">
                        <div>
                            <Text>{'Hai bisogno di aiuto?'}</Text>
                            <Link
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleClickReport();
                                }}
                                disabled={!onlineStatus}
                            >
                                {'Segnala un problema'}
                            </Link>
                        </div>
                        <div>
                            <Text>{'Ci aiuti a migliorare?'}</Text>
                            <Link
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    showModal(RATING_MODAL);
                                }}
                                disabled={!onlineStatus}
                            >
                                {'Lascia il tuo parere'}
                            </Link>
                        </div>
                    </Stack>
                </Section>
                <Copyright>
                    <span>{`Copyright © Zanichelli ${BUILD_YEAR}`}</span>
                </Copyright>
            </Footer>
        </Container>
    );
};

SidebarContainer.propTypes = {
    applicationStatus: PropTypes.object,
    isPreview: PropTypes.bool,
    assignment: PropTypes.object,
    currentIndex: PropTypes.number,
    exercises: PropTypes.array,
    publicTitle: PropTypes.string,
    showModal: PropTypes.func,
    hideReport: PropTypes.func,
    showReport: PropTypes.func,
    status: PropTypes.object,
    homework: PropTypes.object,
    homeworkStatus: PropTypes.object,
    statusHttpStatus: PropTypes.object,
    authStatus: PropTypes.object,
    displayReport: PropTypes.bool,
    singleAnswer: PropTypes.bool,
    source: PropTypes.string,
    user: PropTypes.object,
};

const mapStateToProps = (state) => {
    return {
        applicationStatus: state.application.status,
        isPreview: state.application.isPreview,
        currentIndex: state.status.currentExerciseIndex,
        exercises: state.homework.exercises,
        publicTitle: state.homework.publicTitle,
        homework: state.homework,
        assignment: state.assignment,
        status: state.status,
        homeworkStatus: state.homework.status,
        statusHttpStatus: state.status.httpStatus,
        authStatus: state.auth.status,
        displayReport: state.modal.showReport,
        source: state.application.source,
        singleAnswer: state.application.singleAnswer,
        user: state.auth,
    };
};

const actionCreators = {
    showModal,
    hideReport,
    showReport,
};

export default connect(mapStateToProps, actionCreators)(SidebarContainer);
